import React, { useRef, useEffect, useState, useMemo } from 'react';
import { MdMenu, MdClose } from "react-icons/md";
import assets from "../assets/assets";
import SearchSuggestionList from '../Body/Components/SearchSuggestionList';
import { useSearchEngine } from '../Body/Public/useSearch';
import { useAuth, type User } from '../Context/AuthContext';
import MenuDropdown from './MenuComponent';
import { IoSearch } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import { slugify } from '../Body/Public/Explore/services';

// Logo Component
export const LogoIcon = ({ size=7 }) => (
    <img src={assets.logo_icon} className={`h-${size} w-${size}`} alt="Gemio Logo"/>
);

interface HeaderProps {
  showSearch?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showSearch = false }) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const location = useLocation();
  const path = location.pathname;
  
  const { openAuthModal, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    query,
    setQuery,
    suggestions,
    selectedFilters,
    clearAllFilter,
    submitSearch,
    toggleFilter,
    loading,
  } = useSearchEngine();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close search suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };
    if (isFocused) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocused]);

  const switchText = useMemo(() => {
    const hasRestaurants = !!(user && user.savedRestaurants?.length > 0);

    // Case 1 — User has no restaurants → ONLY show "Add your gem" on "/"
    if (!hasRestaurants && path === "/") {
      return { text: "Add your gem", nav: "/dashboard" };
    }

    // Case 2 — Explore pages
    if (path === "/" || path.startsWith("/restaurant/")) {
      return { text: "Switch to managing", nav: "/dashboard" };
    }

    // Case 3 — Dashboard home
    if (path === "/dashboard") {
      return { text: "Switch to exploring", nav: "/" };
    }

    // Case 4 — Dashboard subpages
    if (path.startsWith("/dashboard")) {
      return { text: "Switch to exploring", nav: "/" };
    }

    // Fallback
    return { text: "Switch to exploring", nav: "/" };
  }, [user, path]);
  
  const handleClick = () => {
    if (authLoading) return;
    if (!user) return openAuthModal();
    if (path === switchText.nav && path === "/dashboard") {
      return navigate("/dashboard/create");
    }

    navigate(switchText.nav);
  };


  return (
    <div className={`fixed z-[50] bg-[#FFF8E7] w-full flex items-center justify-between border-gray-300 px-2 md:px-8 transition-all duration-300 ${showSearch ? 'h-20 border-b shadow' : 'h-17'}`}>
      
      {/* Left: Logo */}
      <div className={`flex items-center transition-all duration-300 cursor-pointer ${isFocused ? 'hidden md:flex' : 'flex'}`} onClick={() => navigate("/")}>
        <LogoIcon />
        <a href='/' className='Logo-sans font-semibold ml-1 text-2xl text-[#ee5219]'>Gemio</a>
      </div>

      {/* Back button on mobile */}
      {isFocused && (
        <button
          onClick={() => setIsFocused(false)}
          className="flex items-center justify-center md:hidden flex-shrink-0 w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
        >
          <MdClose className="w-6 h-6 text-gray-600" />
        </button>
      )}

      {/* Search Bar */}
      {showSearch && (
        <div ref={searchContainerRef} className={`flex-1 transition-all duration-300 ${isFocused ? 'mx-2 md:mx-4 md:max-w-2xl' : 'mx-4 max-w-md'}`}>
          <div className="relative">
            <input
              type="text"
              placeholder="Search for restaurants, cuisines, or areas..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              className={`w-full border border-[#FFDDA6] rounded-xl focus:ring-2 focus:ring-[#FFD166] focus:border-transparent transition-all bg-white ${isFocused ? 'pl-10 pr-10 py-3 text-base' : 'pl-10 pr-4 py-2 text-sm'}`}
            />
            {/* Clear button */}
            {query && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-4" onClick={() => submitSearch(query)}>
                <IoSearch size={20} className='text-gray-400 hover:text-gray-600'/>
              </div>
            )}

            <button
              onClick={() => {
                  query ? setQuery("") : setIsFocused(false)
              }}
              className="absolute inset-y-0 left-0 flex items-center pl-3 rounded-r-xl transition-colors"
            >
              {isFocused ?                 
                <MdClose className="w-5 h-5 text-gray-400 hover:text-gray-600" /> :
                <IoSearch className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              }
            </button>
            

            {/* Search suggestions dropdown */}
            <SearchSuggestionList
              query={query}
              isFocused={isFocused}
              loading={loading}
              suggestions={suggestions}
              selectedFilters={selectedFilters}
              onFilterToggle={toggleFilter}
              onWordClick={(word) => setQuery(word)}
              onRestaurantClick={(restaurant) => window.location.href = `/restaurant/${restaurant._id}/${slugify(restaurant.name)}`}
              onClearFilters={clearAllFilter}
            />
          </div>
        </div>
      )}

      {/* Right: Menu */}
      <div className={`relative flex items-center space-x-2 transition-all duration-300 ${isFocused ? 'hidden md:flex' : 'flex'}`} ref={menuRef}>
        {/* <button className="hidden sm:block text-[#5E503F] hover:text-[#111827] bg-white border border-[#FFDDA6] hover:border-[#FFD166] px-4 py-2 rounded-full text-sm font-medium shadow-sm hover:shadow transition-all" onClick={handleClick}>
          {switchText.text}
        </button> */}
        <button onClick={() => setMenuOpen(prev => !prev)} className="flex items-center justify-center border border-[#FFDDA6] rounded-full p-2 hover:bg-[#FFF8E7] hover:shadow transition-all">
          {menuOpen ? <MdClose size={20} className="text-[#5E503F]" /> : <MdMenu size={20} className="text-[#5E503F]" />}
        </button>

        <MenuDropdown
          menuOpen={menuOpen}
          switchText={switchText}
          setMenuOpen={setMenuOpen}
        />
      </div>
    </div>
  );
};

export default Header;
